import { z } from "zod";
import { safeGetItem } from "./storage.helpers";

// The part of a NetworkError message that is stable enough to match on.
// Exported so callers that filter on the message (the PostHog noise list in
// main.tsx) are tied to the format instead of duplicating the literal — the
// trailing statusText is left out because HTTP/2 always reports it empty.
export function networkErrorPrefix(endpoint: string, statusCode: number) {
  return `API error ${endpoint}: ${statusCode}`;
}

// `method` is the request method when known (apiGenericRequest), so a GET
// answered by a gateway error does not ask whether a change was saved
// (errors.helpers.ts). Optional so 4-argument constructions keep working.
export class NetworkError extends Error {
  Response: any;
  StatusCode: number;
  readonly method?: string;

  constructor(
    statusCode: number,
    statusText: string,
    endpoint: string,
    body: any,
    method?: string,
  ) {
    super(`${networkErrorPrefix(endpoint, statusCode)} ${statusText}`);
    this.StatusCode = statusCode;
    this.Response = body;
    this.method = method;
  }
}

export function unreachableErrorPrefix(endpoint: string) {
  return `Server unreachable ${endpoint}`;
}

// A fetch() rejection (offline, DNS, CORS, reset). Deliberately not a
// NetworkError subclass so the `StatusCode === 401` logout check never matches.
// The request details let the mutation toast say only what is provable
// (errors.helpers.ts).
export class UnreachableError extends Error {
  readonly method?: string;
  readonly offlineAtSend: boolean;

  constructor(
    endpoint: string,
    cause?: unknown,
    request?: { method?: string; offlineAtSend?: boolean },
  ) {
    super(unreachableErrorPrefix(endpoint), { cause });
    this.name = "UnreachableError";
    this.method = request?.method;
    this.offlineAtSend = request?.offlineAtSend ?? false;
  }
}

// Single place that reads the API base URL. Switch this line to config.apiUri
// when main is merged into offlinebuild.
export function apiBaseUrl(): string {
  return import.meta.env.VITE_API_URI ?? "";
}

async function apiFetch(
  endpoint: string,
  url: string,
  init: RequestInit,
): Promise<Response> {
  // Read before fetch(): an offline browser at send time is the only proof that
  // the request never left. Going offline during the request proves nothing.
  const method = (init.method ?? "GET").toUpperCase();
  const offlineAtSend =
    typeof navigator !== "undefined" && navigator.onLine === false;
  try {
    return await fetch(url, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new UnreachableError(endpoint, error, { method, offlineAtSend });
  }
}

export class ErrorResponse extends Error {
  Response: any;

  constructor(response: any) {
    super(`Error Response: ${response.toString()}`);
    this.Response = response;
  }
}

async function apiGenericRequest(
  method: string,
  endpoint: string,
  extraArgs: any,
  body: any,
): Promise<any> {
  const token = safeGetItem("authentication_token");
  const schoolID = extraArgs?.school_id ? undefined : safeGetItem("school_id");

  let querySchoolID = schoolID ? `&school_id=${schoolID}` : "";

  let query = `?token=${encodeURIComponent(token ?? "")}${querySchoolID}`;
  for (let key in extraArgs) {
    if (extraArgs.hasOwnProperty(key)) {
      query += `&${encodeURIComponent(key)}=${encodeURIComponent(extraArgs[key])}`;
    }
  }

  if (body !== undefined) body = JSON.stringify(body);
  const response = await apiFetch(
    endpoint,
    `${apiBaseUrl()}/frontend${endpoint}${query}`,
    { method, body },
  );

  let json;

  if (response.headers.get("content-type")?.includes("application/json")) {
    json = await response.json().catch(() => undefined);
  }

  if (!response.ok) {
    throw new NetworkError(
      response.status,
      response.statusText,
      endpoint,
      json,
      method,
    );
  }

  if (json === null || json === undefined) {
    // 200 without a usable JSON body: proxy/CDN error page, captive portal,
    // empty body, or a handler that marshalled a nil value as literal `null`.
    // Surface as a network error instead of a TypeError on json.Status below.
    throw new NetworkError(
      response.status,
      "Invalid or empty response body",
      endpoint,
      json,
      method,
    );
  }

  if (json.Status !== 1) throw new ErrorResponse(json.Error);

  return json.Data;
}

// For the helpers that hand back the raw Response: without this a 4xx/5xx
// resolves, so a failed upload reads as success and a download saves the
// error body as the file.
async function throwIfNotOk(
  response: Response,
  endpoint: string,
  method: string,
) {
  if (response.ok) return;
  const body = response.headers
    .get("content-type")
    ?.includes("application/json")
    ? await response.json().catch(() => undefined)
    : undefined;
  throw new NetworkError(
    response.status,
    response.statusText,
    endpoint,
    body,
    method.toUpperCase(),
  );
}

export async function apiFileUpload(
  endpoint: string,
  { file, extraArgs }: { file: File; extraArgs?: any },
): Promise<any> {
  const formData = new FormData();
  const token = safeGetItem("authentication_token");
  formData.append("file", file);

  let query = `?token=${encodeURIComponent(token ?? "")}`;
  for (let key in extraArgs) {
    if (extraArgs.hasOwnProperty(key)) {
      query += `&${encodeURIComponent(key)}=${encodeURIComponent(extraArgs[key])}`;
    }
  }

  const response = await apiFetch(
    endpoint,
    `${apiBaseUrl()}/frontend${endpoint}${query}`,
    { method: "post", body: formData },
  );

  await throwIfNotOk(response, endpoint, "post");
  return response;
}

export async function apiStreamRequest(
  method: string,
  endpoint: string,
  fileName: string,
  extraArgs: any,
  body?: any,
): Promise<any> {
  const token = safeGetItem("authentication_token");
  const schoolID = extraArgs?.school_id ? undefined : safeGetItem("school_id");

  let querySchoolID = schoolID ? `&school_id=${schoolID}` : "";

  let query = `?token=${encodeURIComponent(token ?? "")}${querySchoolID}`;
  for (let key in extraArgs) {
    if (extraArgs.hasOwnProperty(key)) {
      query += `&${encodeURIComponent(key)}=${encodeURIComponent(extraArgs[key])}`;
    }
  }

  if (body !== undefined) body = JSON.stringify(body);
  const response = await apiFetch(
    endpoint,
    `${apiBaseUrl()}/frontend${endpoint}${query}`,
    { method, body },
  );

  await throwIfNotOk(response, endpoint, method);
  return response;
}

export function apiGet(endpoint: string, extraArgs?: any): Promise<any> {
  return apiGenericRequest("GET", endpoint, extraArgs, undefined);
}

/**
 * apiGet with runtime validation at the queryFn boundary. A response that
 * doesn't match the schema throws inside the queryFn — surfacing as isError
 * plus telemetry with an exact field path, never as a crash mid-render.
 * Prefer this over `useQuery<T>` type annotations, which are unchecked.
 */
export async function apiGetParsed<S extends z.ZodTypeAny>(
  endpoint: string,
  schema: S,
  extraArgs?: any,
): Promise<z.infer<S>> {
  return schema.parse(await apiGet(endpoint, extraArgs));
}

// The Go backend marshals empty slices/maps as null. Normalize at the schema
// boundary so components never need defensive ?. around collections.
export const goArray = <S extends z.ZodTypeAny>(item: S) =>
  z
    .array(item)
    .nullish()
    .transform((v) => v ?? []);

export function apiPost(
  endpoint: string,
  body: any,
  extraArgs?: any,
): Promise<any> {
  return apiGenericRequest("POST", endpoint, extraArgs, body);
}

export function apiDelete(endpoint: string, body: any): Promise<any> {
  return apiGenericRequest("DELETE", endpoint, undefined, body);
}

export function apiPut(endpoint: string, body: any): Promise<any> {
  return apiGenericRequest("PUT", endpoint, undefined, body);
}

export function apiUnauthenticatedPost(
  endpoint: string,
  body: any,
): Promise<any> {
  return apiGenericRequest("POST", endpoint, null, body);
}
